<template>
  <div class="header">
    <div class="md-small menu-bar">
      <div class="md-toolbar-row">
        <!-- Anonymous menu -->
        <MenuLinks v-if="!user" :links="getAnonymousUserLinks" />

        <!-- Employee menu -->
        <MenuLinks
          v-if="isUpwardFundNominee"
          :links="getNomineeLinks"
        />
        <MenuLinks
          v-else-if="isNorthwesternNominee"
          :links="getNorthwesternNomineeLinks"
        />
        <MenuLinks
          v-else-if="isEmployee"
          :links="getEmployeeLinks"
        />

        <!-- Staff menu -->
        <!--<MenuLinks v-if="user && user.isStaff" :links="getStaffLinks" :footer="false" />-->
        <template v-if="user && user.isStaff">
          <div class="md-toolbar-section-start">
            <!-- Mobile Menu -->
            <md-menu md-size="big" md-align-trigger :mdCloseOnClick="true" :mdCloseOnSelect="true">
              <md-button class="mobile-menu-btn md-icon-button" md-menu-trigger>
                <md-icon class="menuIcon">menu</md-icon>
              </md-button>
              <md-menu-content
                :style="{ background: 'white', maxHeight: 'unset', maxWidth: '100vw' }"
                class="dropdown"
              >
                <template v-for="link in getStaffLinks">
                  <md-menu-item @click="onClickLink(link)">
                    <span class="item">{{ link.label }}</span>
                  </md-menu-item>
                </template>
              </md-menu-content>
            </md-menu>
            <!-- End Mobile Menu -->

            <!-- Desktop Menu -->
            <div class="md-small-hide">
              <md-menu style="display: flex;">
                <template v-for="link in getStaffLinks">
                  <md-button @click="onClickLink(link)" class="desktop-menu-btn">
                    <span class="item">{{ link.label }}</span>
                  </md-button>
                </template>
              </md-menu>
            </div>
            <!-- End Desktop Menu -->
          </div>

          <div class="md-toolbar-section-end md-small-hide">
            <!-- TODO William 2020-03-24 - Add profile image dropdown menu -->
            <md-menu>
              <md-button @click="handleLogout" class="desktop-menu-btn">
                <span class="item" v-translate>LOG OUT</span>
              </md-button>
            </md-menu>
          </div>
        </template>

        <!-- Admin menu -->
        <MenuLinks v-if="user && user.isAdmin" :links="getAdminLinks" :footer="false" />

        <!-- Third-party menu -->
        <MenuLinks v-if="user && user.isThirdParty" :links="getThirdPartyLinks" />

        <!-- Logout -->
        <div class="md-toolbar-section-end" v-if="user && !user.isStaff">
          <md-menu>
            <button class="logout-button" @click="handleLogout">
              <span v-translate>LOG OUT</span>
            </button>
          </md-menu>
        </div>

        <!-- Language preferences -->
        <div class="md-toolbar-section-end" v-if="!user && enableLangPref">
          <md-menu>
            <!-- Languyage preferences button -->
            <button
              v-if="!showLangPrefsForm"
              class="language-prefs-button"
              @click="toggleLangPrefsForm"
            >
              <div class="desktop-menu-toggle">
                <img :src="require('@/assets/icons/globe.png')" alt="languages" />
                <span>{{ languages[$language.current] }}</span>
              </div>
            </button>

            <!-- Language preferences form -->
            <div
              class="lang-prefs-form"
              v-if="showLangPrefsForm"
              v-click-outside="toggleLangPrefsForm"
            >
              <div class="radio-buttons">
                <label v-for="(localeName, localeCode) in languages" class="radio-button">
                  <input
                    type="radio"
                    name="preferredLanguage"
                    v-model="$language.current"
                    value="localeCode"
                    @change="onToggleLanguage(localeCode)"
                  />
                  <span>{{ localeName }}</span>
                </label>
              </div>
            </div>
          </md-menu>
        </div>
      </div>
    </div>
    <div id="menuContainer" class="mobile"></div>
  </div>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import AdminPermissions from '@/services/adminPermissions'
import Auth from '@/services/auth'
import { trackEvent } from '@/services/googleAnalytics'
import MenuLinks from '@/components/menu/MenuLinks'
import { languages } from '../../settings.json'

const ADMIN_LINKS = [
  {
    label: 'REPORTS',
    route: { name: 'StaffReports' },
    permissionRequired: {
      permission: 'canViewReports',
      allowedValues: [true],
    },
  },
  {
    label: '', // set when retrieved, need use of 'this' in order to set value
    route: { name: 'StaffEmployeesOverview' },
    permissionRequired: {
      permission: 'canViewEmployees',
      allowedValues: [true],
    },
  },
  {
    label: 'REQUESTS',
    route: { name: 'StaffRequestsOverview' },
    permissionRequired: {
      permission: 'viewWhichRequests',
      allowedValues: [
        AdminPermissions.options.ALL_REQUESTS,
        AdminPermissions.options.REFERRED_REQUESTS_ONLY,
      ],
    },
  },
  {
    label: 'UPLOAD LIST',
    route: { name: 'StaffEligibilityUploader' },
    permissionRequired: {
      permission: 'canViewEmployeeCodes',
      allowedValues: [true],
    },
  },
  {
    label: 'SFTP FEED',
    route: { name: 'StaffEligibilityFileFeed' },
    permissionRequired: {
      permission: 'canViewEmployeeCodes',
      allowedValues: [true],
    },
  },
]

const STAFF_LINKS = [
  { label: 'REPORTS', route: { name: 'StaffReports' } },
  { label: 'DASHBOARD', route: { name: 'StaffCanaryDashboard' } },
  { label: 'USERS', route: { name: 'StaffEmployeesOverview' } },
  { label: 'ORGANIZATIONS', route: { name: 'StaffOrganizationsOverview' } },
  { label: 'REQUESTS', route: { name: 'StaffRequestsOverview' } },
  { label: 'STIPEND REQUESTS', route: { name: 'StaffStipendRequestsOverview' } },
  { label: 'NOMINATIONS', route: { name: 'StaffNominationsOverview' } },
  { label: 'DISBURSEMENTS', route: { name: 'StaffPayoutsOverview' } },
  { label: 'UPLOAD LIST', route: { name: 'StaffEligibilityUploader' } },
  { label: 'SFTP FEED', route: { name: 'StaffEligibilityFileFeed' } },
]

const THIRD_PARTY_LINKS = [
  { label: 'HOME', route: { name: 'ThirdPartyHome' } },
]

export default {
  name: 'Header',
  components: {
    MenuLinks,
  },
  data() {
    return {
      isCollapsed: false,
      ADMIN_LINKS,
      STAFF_LINKS,
      THIRD_PARTY_LINKS,
      languages: this.$store.state.organizationDetails.languages,
      enableLangPref: this.$store.state.organizationDetails.enableLanguagePref,
      showLangPrefsForm: false,
    }
  },
  methods: {
    onToggleLanguage(value) {
      this.$store.dispatch('updateCurrentLanguage', value)
      this.toggleLangPrefsForm()
    },
    async handleLogout() {
      await Auth.logout()
      location.reload(true)

      /*
      if (this.$route.name != 'Home') {
        this.$router.push({ name: 'Home' })
        this.toggleDropDownMenu()
      }
      */
    },
    startRequest() {
      if (
        this.organizationDetails.isDefaultRequestOrganization &&
        this.organizationDetails.isReproductiveRightsOrganization
      ) {
        return { name: 'StartRequest' }
      }

      if (
        !this.organizationDetails.isDefaultRequestOrganization &&
        this.organizationDetails.isReproductiveRightsOrganization
      ) {
        return { name: 'ReceiveMoney', params: { reproductive_rights: true } }
      }

      if (
        this.organizationDetails.isDefaultRequestOrganization &&
        !this.organizationDetails.isReproductiveRightsOrganization
      ) {
        return { name: 'NewRequest' }
      }

      return { name: 'NewRequest' }
    },
    toggleDropDownMenu() {
      this.isCollapsed = !this.isCollapsed
    },
    /* Return True if adminPermissions contains allowed value for required permission. */
    adminHasPermission(link) {
      // If link doesn't have any specific admin permission requirements, return true
      if (!link.permissionRequired) {
        return true
      }
      return link.permissionRequired.allowedValues.includes(
        this.user?.adminPermissions[link.permissionRequired.permission],
      )
    },
    toggleLangPrefsForm() {
      this.showLangPrefsForm = !this.showLangPrefsForm
    },
    /* Return True if organization has the correct feature enabled to see a menu link. */
    organizationHasFeature(link) {
      // If link doesn't have any specific feature requirements, return true
      if (!link.featureRequired) {
        return true
      }
      return link.featureRequired.allowedValues.includes(
        this.organizationDetails[link.featureRequired.field],
      )
    },
    onClickLink(link) {
      if (link.googleAnalytics) {
        trackEvent(link.googleAnalytics.action, '', 'Menu', '')
      }

      if ('action' in link) {
        link.action()
      } else {
        // If router throws an error because of a route guard redirection, just log it
        this.$router
          .push({ name: link.route.name, params: link.route && link.route.params })
          .catch(error => console.log(error.message))
      }
    },
    isCurrentRoute(link) {
      return this.$route.name == link.route.name
    },
  },
  computed: {
    ...mapState(['user', 'organizationDetails']),
    ...mapGetters(['userIsLoggedIn']),
    /* Return all menu links appropriate for an anonymous user. */
    getAnonymousUserLinks() {
      return this.anonymousUserLinks.filter(link => {
        return this.organizationHasFeature(link) ? true : false
      })
    },
    /* Return all menu links appropriate for an employee. */
    getEmployeeLinks() {
      return this.employeeLinks.filter(link => {
        return this.organizationHasFeature(link) ? true : false
      })
    },
    getNomineeLinks() {
      return this.nomineeLinks
    },
    getNorthwesternNomineeLinks() {
      return this.northwesternNomineesLinks
    },
    /* Return all menu links appropriate for an admin. */
    getAdminLinks() {
      var admin_menu = [...this.ADMIN_LINKS]
      admin_menu[1]['label'] =
        this.organizationDetails.customVariables && this.organizationDetails.customVariables.cv3
          ? (this.organizationDetails.customVariables.cv3 + 's').toUpperCase()
          : 'USERS'

      // Check if Upward Fund, if so then add nominations link
      if (this.organizationDetails.isTcrOrganization) {
          // Remove non-UF related links
          let requestIndex = admin_menu.findIndex(link => link.label === 'REQUESTS')
          admin_menu.splice(requestIndex, 1)

          let uploadListIndex = admin_menu.findIndex(link => link.label === 'UPLOAD LIST')
          admin_menu.splice(uploadListIndex, 1)

          let sftpFeedIndex = admin_menu.findIndex(link => link.label === 'SFTP FEED')
          admin_menu.splice(sftpFeedIndex, 1)

          admin_menu.splice(0, 0, {
            label: 'HOME',
            route: { name: 'PartnersHome' },
            permissionRequired: {
              permission: 'canViewNominations',
              allowedValues: [true],
            },
          })
          admin_menu.push({
            label: 'STIPEND REQUESTS',
            route: { name: 'StaffStipendRequestsOverview' },
            permissionRequired: {
              permission: 'canViewStipendRequests',
              allowedValues: [true],
            },
          })
          admin_menu.push({
            label: 'NOMINATIONS',
            route: { name: 'StaffNominationsOverview' },
            permissionRequired: {
              permission: 'canViewNominations',
              allowedValues: [true],
            },
          })
      } else if(this.organizationDetails.isNorthwestern) {
        admin_menu.splice(0, 0, {
          label: 'HOME',
          route: { name: 'NorthwesternAdminHome' },
          permissionRequired: {
            permission: 'canViewNominations',
            allowedValues: [true],
          },
        })
        admin_menu.push({
          label: 'NOMINATIONS',
          route: { name: 'StaffNominationsOverview' },
          permissionRequired: {
            permission: 'canViewNominations',
            allowedValues: [true],
          },
        })
      }

      return admin_menu.filter(link => {
        return this.adminHasPermission(link) ? true : false
      })
    },
    /* Return all menu links appropriate for a staff. */
    getStaffLinks() {
      return this.STAFF_LINKS
    },
    getThirdPartyLinks() {
      return this.THIRD_PARTY_LINKS
    },
    anonymousUserLinks() {
      return [
        { label: this.$gettext('HOME'), route: { name: 'Home' } },
        { label: this.$gettext('FAQ'), route: { name: 'FAQ' } },
        {
          label: this.$gettext('GIVE'),
          route: { name: 'AddPayment' },
          actionName: "goto-donate",
          featureRequired: { field: 'donationsEnabled', allowedValues: [true] },
        },
        {
          label: this.$gettext('FIND RESOURCES'),
          route: { name: 'FindResources' },
          featureRequired: { field: 'springFourEnabled', allowedValues: [true] },
          googleAnalytics: { action: 'clicked-resources-cta' },
        },
        { label: this.$gettext('SIGN IN'), route: { name: 'Login' }, actionName: "goto-login" },
      ]
    },
    isEmployee() {
      return this.user && this.user.isEmployee && !this.user.isStaff && !this.user.isAdmin
    },
    employeeLinks() {
      return [
        { label: this.$gettext('HOME'), route: { name: 'Home' } },
        { label: this.$gettext('REQUEST'), route: this.startRequest(), actionName: "goto-request" },
        {
          label: this.$gettext('GIVE'),
          route: { name: 'AddPayment' },
          actionName: "goto-donate",
          featureRequired: { field: 'donationsEnabled', allowedValues: [true] },
        },
        {
          label: this.$gettext('FIND RESOURCES'),
          route: { name: 'FindResources' },
          featureRequired: { field: 'springFourEnabled', allowedValues: [true] },
          googleAnalytics: { action: 'clicked-resources-cta' },
        },
        { label: this.$gettext('MY ACTIVITY'), route: { name: 'MyActivity' } },
        { label: this.$gettext('PROFILE'), route: { name: 'MyProfile' } },
        { label: this.$gettext('FAQ'), route: { name: 'FAQ' } },
        { label: this.$gettext('LOG OUT'), route: { name: '' }, action: this.handleLogout },
      ]
    },
    isUpwardFundNominee() {
      return this.user && this.user.isEmployee && this.organizationDetails.isTcrOrganization && !this.user.isStaff && !this.user.isAdmin
    },
    nomineeLinks() {
      return [
        { label: this.$gettext('HOME'), route: { name: 'NomineeHome' } },
        { label: this.$gettext('MY ACTIVITY'), route:{ name: 'MyActivity' } },
        { label: this.$gettext('PROFILE'), route: { name: 'MyProfile' } },
        { label: this.$gettext('FAQ'), route: { name: 'FAQ' } },
      ]
    },
    isNorthwesternNominee() {
      return this.user && this.user.isEmployee && this.organizationDetails.isNorthwestern && !this.user.isStaff && !this.user.isAdmin
    },
    northwesternNomineesLinks() {
      return [
        { label: this.$gettext('HOME'), route: { name: 'NorthwesternNomineeHome' } },
        { label: this.$gettext('MY ACTIVITY'), route:{ name: 'MyActivity' } },
        { label: this.$gettext('FAQ'), route: { name: 'FAQ' } },
      ]
    },
  },
}
</script>

<style>

.v-menu__content.main-menu {
  max-width: 100vw;
}

</style>

<style lang="scss" scoped>
@import '../styles/mixins/spinners';
@import '../styles/variables';
@import '@/styles/mixins/responsive';

#menuContainer {
  max-width: 100vw;
  z-index: 12;
}

.header {
  position: sticky;
  top: 0;
  z-index: 10;

  .menu-bar {
    background: white;
    padding: 0 1rem;
  }

  @include tablet-and-up-screen {
    position: inherit;
  }
}

.dropdown {
  z-index: 11;
  padding-left: 0.5rem;

  @media (min-width: $mobile-break-point) {
    display: none;
  }
}

.mobile-menu-btn {
  @media (min-width: $mobile-break-point) {
    display: none;
  }
}

.desktop-menu-btn {
  .item {
    color: black;
    font-weight: bold;
  }
}

.desktop-menu-toggle {
  display: flex;
  align-items: center;

  img {
    height: 1rem;
    padding-right: 0.5em;
  }
}

.md-toolbar,
.md-toolbar-row {
  // NIP 10/19/20 HACK: VueMaterial toolbars change height based on screenwidth inside a media query.
  //                    We need to overwrite this and keep our height consistent, so we can subtract
  //                    against this height to generate $compensated-screen-height
  @include mobile-screen {
    min-height: $app-header-height;
    max-height: $app-header-height;
  }

  @include tablet-and-up-screen {
    height: 60px;
  }
}

.menuIcon {
  font-size: 1.5625rem;
  margin: 1rem;
  color: black;
  outline: none;
}

.item {
  color: $black;
  cursor: pointer;
}

.item:hover {
  text-decoration: underline;
}

ul {
  list-style: none;
}

li {
  color: $black;
  padding: 0.625rem 0.9375rem;
}

.underline {
  text-decoration: underline;
}

.language-prefs-button,
.logout-button {
  border: 1px solid black;
  border-radius: 4px;
  padding: 4px 12px;
  text-transform: none;

  span {
    color: #212b36;
    font-weight: bold;
    font-family: 'SFPro-Regular';
    font-size: 0.9rem;
  }
}

.lang-prefs-form {
  background: white;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(6, 44, 82, 0.1), 0 2px 16px 0 rgba(33, 43, 54, 0.08);
  color: #212b36;
  padding: 14px;
  position: relative;
  top: 5.75rem;

  .radio-buttons .radio-button + .radio-button {
    margin-top: 0.5rem;
  }
}
</style>
